// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Calendar, MapPin, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import EventDetailScreen from './EventDetailScreen';
import { parseLocalDate } from '@/utils/formatters';

interface MyEventsScreenProps {
  selectedEventId: string | null;
  onSelectEvent: (eventId: string) => void;
  onBack: () => void;
  userEmail?: string;
  profileId?: string;
}

interface UserEvent {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue_city: string;
  venue_state: string;
  thumbnail_url: string | null;
  teamCount?: number;
}

// Build full image URL from thumbnail filename
const getImageUrl = (thumbnail: string | null): string | null => {
  if (!thumbnail) return null;
  if (thumbnail.startsWith('http')) return thumbnail;
  return `https://kvttsygfutwcywroraji.supabase.co/storage/v1/object/public/event-assets/${String(thumbnail).replace(/^\/+/, '')}`;
};

const MyEventsScreen: React.FC<MyEventsScreenProps> = ({ 
  selectedEventId, 
  onSelectEvent,
  onBack,
  userEmail,
  profileId
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ['my-events-mobile', userEmail, profileId],
    queryFn: async (): Promise<UserEvent[]> => {
      console.log('[MyEvents] Fetching registrations for:', { email: userEmail, profileId: profileId });
      
      if (!userEmail && !profileId) {
        console.log('[MyEvents] No email or profileId provided, returning empty');
        return [];
      }

      const allEventIds: Set<string> = new Set();
      const eventTeamCounts: Map<string, number> = new Map();

      // Query 1: Get registrations by profile_id from new registrations table
      if (profileId) {
        console.log('[MyEvents] Query 1: Fetching registrations by profile ID:', profileId);
        const { data: regByProfileId, error: error1 } = await supabase
          .from('registrations')
          .select('id, event_id, registration_type')
          .or(`registrant_data->>user_id.eq.${profileId},registrant_data->>coach_user_id.eq.${profileId},registrant_data->>profile_id.eq.${profileId}`)
          .in('status', ['confirmed', 'pending']);

        console.log('[MyEvents] Query 1 result:', regByProfileId, 'Error:', error1);

        if (regByProfileId) {
          regByProfileId.forEach((r: any) => {
            if (r.event_id) {
              allEventIds.add(r.event_id);
              // Count teams per event
              if (r.registration_type === 'team' || r.registration_type === 'coach') {
                eventTeamCounts.set(r.event_id, (eventTeamCounts.get(r.event_id) || 0) + 1);
              }
            }
          });
        }

        // Also check registered_by field directly
        const { data: regByUser, error: errorReg } = await supabase
          .from('registrations')
          .select('id, event_id, registration_type')
          .eq('registered_by', profileId)
          .in('status', ['confirmed', 'pending']);

        console.log('[MyEvents] Query 1b (registered_by) result:', regByUser, 'Error:', errorReg);

        if (regByUser) {
          regByUser.forEach((r: any) => {
            if (r.event_id) {
              allEventIds.add(r.event_id);
              if (r.registration_type === 'team' || r.registration_type === 'coach') {
                eventTeamCounts.set(r.event_id, (eventTeamCounts.get(r.event_id) || 0) + 1);
              }
            }
          });
        }
      }

      // Query 2: Get registrations by email from new registrations table
      if (userEmail) {
        console.log('[MyEvents] Query 2: Fetching registrations by email:', userEmail);
        const { data: regByEmail, error: error2 } = await supabase
          .from('registrations')
          .select('id, event_id, registration_type')
          .or(`registrant_data->>email.eq.${userEmail},registrant_data->>contact_email.eq.${userEmail},registrant_data->coach->>email.eq.${userEmail},registrant_data->athlete->>email.eq.${userEmail}`)
          .in('status', ['confirmed', 'pending']);

        console.log('[MyEvents] Query 2 result:', regByEmail, 'Error:', error2);

        if (regByEmail) {
          regByEmail.forEach((r: any) => {
            if (r.event_id) {
              allEventIds.add(r.event_id);
              if (r.registration_type === 'team' || r.registration_type === 'coach') {
                eventTeamCounts.set(r.event_id, (eventTeamCounts.get(r.event_id) || 0) + 1);
              }
            }
          });
        }
      }

      // Query 3: Coach invitations (for coaches invited to teams)
      if (userEmail) {
        console.log('[MyEvents] Query 3: Fetching coach invitations for email:', userEmail);
        const { data: coachInvitations, error: error3 } = await supabase
          .from('coach_invitations')
          .select('id, registration_id')
          .eq('coach_email', userEmail)
          .eq('status', 'accepted');

        console.log('[MyEvents] Query 3 result:', coachInvitations, 'Error:', error3);

        if (coachInvitations && coachInvitations.length > 0) {
          // Get the registrations for these invitations
          const regIds = coachInvitations.map((inv: any) => inv.registration_id).filter(Boolean);
          if (regIds.length > 0) {
            const { data: invRegs, error: invRegsError } = await supabase
              .from('registrations')
              .select('id, event_id, registration_type')
              .in('id', regIds);
            
            console.log('[MyEvents] Query 3b - invitation registrations:', invRegs, 'Error:', invRegsError);
            
            if (invRegs) {
              invRegs.forEach((r: any) => {
                if (r.event_id) {
                  allEventIds.add(r.event_id);
                  // Count teams from invitations
                  eventTeamCounts.set(r.event_id, (eventTeamCounts.get(r.event_id) || 0) + 1);
                }
              });
            }
          }
        }
      }

      // Coach team entries come from `registrations` (event_registrations_v2) above;
      // club_team_event_registrations does not exist in RLE.

      console.log('[MyEvents] All unique event IDs found:', Array.from(allEventIds));
      console.log('[MyEvents] Team counts per event:', Object.fromEntries(eventTeamCounts));

      // Now fetch events separately using the collected event IDs
      if (allEventIds.size === 0) {
        console.log('[MyEvents] No event IDs found, returning empty');
        return [];
      }

      const today = new Date().toISOString().split('T')[0];
      
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, name, start_date, end_date, venue_city, venue_state, thumbnail_url, status')
        .in('id', Array.from(allEventIds))
        .eq('status', 'published')
        .gte('end_date', today);

      console.log('[MyEvents] Events fetched:', eventsData, 'Error:', eventsError);

      if (!eventsData || eventsData.length === 0) {
        console.log('[MyEvents] No events found for the IDs');
        return [];
      }

      // Sort by start date (upcoming first)
      const sortedEvents = eventsData
        .map(event => ({
          ...event,
          teamCount: eventTeamCounts.get(event.id) || 0
        }))
        .sort((a, b) => 
          parseLocalDate(a.start_date).getTime() - parseLocalDate(b.start_date).getTime()
        );

      console.log('[MyEvents] Final events count:', sortedEvents.length);
      return sortedEvents;
    },
    enabled: !!(userEmail || profileId),
  });

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  // Show event detail if selected
  if (selectedEventId) {
    return (
      <EventDetailScreen 
        eventId={selectedEventId} 
        onBack={onBack} 
        userId={profileId}
        userEmail={userEmail}
        profileId={profileId}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-lg font-semibold">My Events</h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className={cn("h-5 w-5", isRefreshing && "animate-spin")} />
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : events && events.length > 0 ? (
          <div className="divide-y divide-border">
            {events.map((event) => (
              <button
                key={event.id}
                onClick={() => onSelectEvent(event.id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
              >
              {(() => {
                const imageUrl = getImageUrl(event.thumbnail_url);
                return (
                  <div className="w-16 h-16 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={event.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                );
              })()}

                {/* Event Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground truncate">
                    {event.name}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      {format(parseLocalDate(event.start_date), 'MMM d')}
                      {event.end_date && event.end_date !== event.start_date && (
                        <> - {format(parseLocalDate(event.end_date), 'MMM d')}</>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    {(event.venue_city || event.venue_state) && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>
                          {[event.venue_city, event.venue_state].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {event.teamCount && event.teamCount > 0 && (
                      <div className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        <Users className="h-3 w-3" />
                        <span>{event.teamCount} team{event.teamCount !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                </div>

                <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">No events found</h3>
            <p className="text-sm text-muted-foreground text-center">
              Events you register for will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEventsScreen;
